import { useEffect, useState } from "react";
import Modal from "@/components/base/Modal";
import {
  inputClass,
  labelClass,
  ghostButtonClass,
  primaryButtonClass,
} from "@/components/base/fieldStyles";
import type { Client } from "@/types";

interface FormState {
  name: string;
  cedula: string;
  phone: string;
  department: string;
  city: string;
  neighborhood: string;
  address: string;
}

const EMPTY: FormState = {
  name: "",
  cedula: "",
  phone: "",
  department: "",
  city: "",
  neighborhood: "",
  address: "",
};

interface ClientFormModalProps {
  open: boolean;
  client: Client | null;
  onClose: () => void;
  onSubmit: (client: Client) => void;
}

const ClientFormModal = ({ open, client, onClose, onSubmit }: ClientFormModalProps) => {
  const [form, setForm] = useState<FormState>(EMPTY);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setError("");
    setForm(
      client
        ? {
            name: client.name,
            cedula: client.cedula,
            phone: client.phone,
            department: client.department ?? "",
            city: client.city ?? "",
            neighborhood: client.neighborhood ?? "",
            address: client.address,
          }
        : EMPTY
    );
  }, [open, client]);

  const set = (patch: Partial<FormState>) => setForm((prev) => ({ ...prev, ...patch }));

  const submit = () => {
    if (!form.name.trim()) {
      setError("El nombre del cliente es obligatorio.");
      return;
    }
    onSubmit({
      id: client?.id ?? "",
      name: form.name.trim(),
      cedula: form.cedula.trim(),
      phone: form.phone.trim(),
      department: form.department.trim(),
      city: form.city.trim(),
      neighborhood: form.neighborhood.trim(),
      address: form.address.trim(),
    });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={client ? "Editar cliente" : "Nuevo cliente"}
      icon="ri-user-3-line"
      footer={
        <>
          <button onClick={onClose} className={ghostButtonClass}>
            Cancelar
          </button>
          <button onClick={submit} className={primaryButtonClass}>
            <i className="ri-save-3-line" /> {client ? "Guardar cambios" : "Crear cliente"}
          </button>
        </>
      }
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2">
          <label className={labelClass}>Nombre completo</label>
          <input
            value={form.name}
            onChange={(e) => set({ name: e.target.value })}
            placeholder="Ej: Nubia Avendaño"
            className={inputClass}
          />
        </div>

        <div>
          <label className={labelClass}>Cédula</label>
          <input
            value={form.cedula}
            onChange={(e) => set({ cedula: e.target.value })}
            placeholder="Documento"
            className={inputClass}
          />
        </div>

        <div>
          <label className={labelClass}>Celular</label>
          <input
            value={form.phone}
            onChange={(e) => set({ phone: e.target.value })}
            placeholder="300 000 0000"
            className={inputClass}
          />
        </div>

        <div>
          <label className={labelClass}>Departamento (DTO)</label>
          <input
            value={form.department}
            onChange={(e) => set({ department: e.target.value })}
            placeholder="Ej: Bogotá D.C."
            className={inputClass}
          />
        </div>

        <div>
          <label className={labelClass}>Ciudad / Municipio</label>
          <input
            value={form.city}
            onChange={(e) => set({ city: e.target.value })}
            placeholder="Ej: Bogotá"
            className={inputClass}
          />
        </div>

        <div>
          <label className={labelClass}>Barrio</label>
          <input
            value={form.neighborhood}
            onChange={(e) => set({ neighborhood: e.target.value })}
            placeholder="Ej: Barrancas"
            className={inputClass}
          />
        </div>

        <div>
          <label className={labelClass}>Dirección</label>
          <input
            value={form.address}
            onChange={(e) => set({ address: e.target.value })}
            placeholder="Dirección de entrega"
            className={inputClass}
          />
        </div>
      </div>

      {error && (
        <p className="text-[11px] text-primary-200 mt-3 flex items-center gap-1.5">
          <i className="ri-error-warning-line" /> {error}
        </p>
      )}
    </Modal>
  );
};

export default ClientFormModal;